<template>
  <div>
    <breadcrumb-button hide-settings>
      <template #beforeDropdown>
        <b-button variant="primary" class="btn-icon mx-1" v-b-tooltip :title="$t('tasks.newTask')"
                  v-if="hasPermission(permissions.TASK_CREATE)" @click="onNew">
          <feather-icon icon="PlusIcon" size="16"/>
        </b-button>
      </template>
    </breadcrumb-button>

    <filter-card v-model="filter" v-bind:fields="filterFields" search/>

    <b-card no-body>
      <b-card-header class="pb-50 tasks-toolbar">
        <h5 class="mb-0">{{ $t('menu.tasks') }}</h5>
        <div class="tasks-switch">
          <button type="button" class="tasks-switch__opt" :class="{ 'is-active': view === 'board' }" @click="view = 'board'">
            <i class="fa-solid fa-table-columns"></i> {{ $t('tasks.board') }}
          </button>
          <button type="button" class="tasks-switch__opt" :class="{ 'is-active': view === 'list' }" @click="view = 'list'">
            <i class="fa-solid fa-list"></i> {{ $t('tasks.list') }}
          </button>
        </div>
      </b-card-header>
      <b-card-body>
        <b-overlay :show="boardLoading && view === 'board'" rounded="lg" opacity="0.45" spinner-variant="primary">
          <task-board v-if="view === 'board'" :tasks="boardTasks"
                      @open="onOpen" @status-change="onStatusChange"/>
          <tasks-table v-else ref="tasksTable" :filters="filter" :lazy="false" @open="onOpen"/>
        </b-overlay>
      </b-card-body>
    </b-card>

    <task-modal ref="taskModal" @submitted="onSubmitted"/>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import BreadcrumbButton from '@/components/widget/breadcrumbButton'
import FilterCard from '@/components/widget/FilterCard'
import TaskBoard from '@/components/tasks/TaskBoard'
import TasksTable from '@/components/tables/TasksTable'
import TaskModal from '@/components/modal/TaskModal'
import TaskSearchRequest from '@/model/task/TaskSearchRequest'
import TaskFilterFields from '@/model/filter/TaskFilterFields'
import mixinBase from '@/components/mixin/mixinBase'
import mixinPermissions from '@/components/mixin/mixinPermissions'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import Permissions from '@/model/Permissions'

export default {
  name: 'TasksView',
  components: { BreadcrumbButton, FilterCard, TaskBoard, TasksTable, TaskModal },
  mixins: [mixinBase, mixinPermissions, mixinNotifications],
  data() {
    return {
      filter: TaskSearchRequest(),
      filterFields: [],
      permissions: Permissions,
      view: 'board',
      boardTasks: [],
      boardLoading: false,
    }
  },
  watch: {
    filter: { deep: true, handler() { if (this.view === 'board') this.loadBoard() } },
    view(v) { if (v === 'board') this.loadBoard() },
    '$route.query.task'(id) { if (id) this.$refs.taskModal.showModal(id) },
  },
  created() {
    this.filterFields = TaskFilterFields(this)
    this.loadBoard()
  },
  mounted() {
    const id = this.$route.query.task
    if (id) this.$nextTick(() => this.$refs.taskModal.showModal(id))
  },
  methods: {
    ...mapActions('tasks', ['getAllTasks', 'changeTaskStatus']),
    loadBoard() {
      this.boardLoading = true
      this.getAllTasks({
        pagination: { page: 1, size: 300 },
        sort: { field: 'creationTime', direction: 'DESC' },
        filter: this.filter,
      }).then(
        res => { this.boardTasks = res.data || [] },
        err => this.showServerErrorNotification('getAllTasks', 'tasks', err)
      ).finally(() => { this.boardLoading = false })
    },
    onNew() { this.$refs.taskModal.showModal(null) },
    onOpen(task) { this.$refs.taskModal.showModal(task.id || task) },
    onStatusChange({ id, status }) {
      this.changeTaskStatus({ id, status }).then(
        () => { /* optimistic move already applied on board */ },
        err => { this.showServerErrorNotification('changeTaskStatus', 'tasks', err); this.loadBoard() }
      )
    },
    onSubmitted() {
      if (this.view === 'board') this.loadBoard()
      else if (this.$refs.tasksTable) this.$refs.tasksTable.refresh()
    },
  },
}
</script>

<style scoped>
.tasks-toolbar { display: flex; align-items: center; justify-content: space-between; }
.tasks-switch { display: inline-flex; background: #f0f1f7; border-radius: 10px; padding: 3px; gap: 3px; }
.tasks-switch__opt {
  border: none; background: transparent; color: #6e6b7b; font-weight: 600; font-size: 0.8rem;
  padding: 0.35rem 0.8rem; border-radius: 8px; cursor: pointer; transition: all 0.15s ease;
}
.tasks-switch__opt.is-active { background: #fff; color: #007bff; box-shadow: 0 2px 7px rgba(20, 24, 50, 0.12); }

/* Dark mode */
body.dark-layout .tasks-switch { background: var(--crm-dark-surface-2, #2e3850); }
body.dark-layout .tasks-switch__opt { color: #b4b7bd; }
body.dark-layout .tasks-switch__opt:hover { color: #fff; }
body.dark-layout .tasks-switch__opt.is-active { background: var(--crm-dark-surface, #232c3e); color: #6cb2ff; }
</style>
